/**
 * Canonical state contract — `docs/06-states-indicators-responsive.md`.
 *
 * Every state maps to exactly one semantic tone. Tones are the *only* legal way
 * for the UI to pick a colour: red is reserved for confirmed fault, blockade or
 * critical incident and can never be produced by a merely "low" value.
 */
export const SEMANTIC_TONES = [
    'neutral', // upcoming / inactive / unknown structure — never green
    'active', // blue: in progress / normal movement
    'success', // green: done / verified / healthy
    'attention', // yellow: attention / uncertainty / stale
    'risk', // orange: rising risk / intervention needed soon
    'critical', // red: confirmed fault, blockade, failure, split-brain
];
/** Ordered by operational urgency, highest last. Used to sort intervention feeds. */
export const TONE_URGENCY = {
    success: 0,
    neutral: 1,
    active: 2,
    attention: 3,
    risk: 4,
    critical: 5,
};
export const PROJECT_STATUSES = [
    'DRAFT',
    'ONBOARDING',
    'ACTIVE',
    'WAITING_DECISION',
    'AT_RISK',
    'BLOCKED',
    'COMPLETED',
    'ARCHIVED',
    'UNKNOWN',
];
/**
 * What a piece of work is, structurally. A slice is a vertical increment; a
 * capability is a horizontal concern several slices contribute to; a station
 * is a point where a person can see and accept something.
 */
export const WORK_ITEM_TYPES = ['SLICE', 'CAPABILITY', 'STATION', 'DECISION', 'SPIKE'];
export const WORK_ITEM_STATUSES = [
    'PLANNED',
    'READY',
    'IN_PROGRESS',
    'WAITING_DEPENDENCY',
    'WAITING_HUMAN',
    'REVIEW',
    'BLOCKED',
    'DONE',
    'SKIPPED',
    'CANCELLED',
];
export const TEAM_MEMBER_STATUSES = [
    'ACTIVE',
    'IDLE_CAPACITY',
    'WAITING',
    'BLOCKED_LIMIT',
    'BLOCKED_OTHER',
    'OFFLINE',
    'UNKNOWN',
];
export const AGENT_STATUSES = [
    'STARTING',
    'WORKING',
    'WAITING',
    'BLOCKED',
    'PAUSED',
    'COMPLETED',
    'FAILED',
    'STALE',
    'STOPPED',
];
export const SESSION_STATUSES = [
    'AVAILABLE',
    'ACTIVE',
    'RANGE_TIGHT',
    'RANGE_EXHAUSTED',
    'RATE_LIMITED',
    'STALE',
    'ENDED',
    'FAILED',
];
export const CONNECTION_STATES = [
    'DRAFT',
    'DISCOVERING',
    'MAPPING_PROPOSED',
    'AWAITING_CONFIRMATION',
    'VERIFYING',
    'INITIAL_SYNC',
    'CONNECTED',
    'DEGRADED',
    'RECONNECTING',
    'FAILED',
    'UNLINKED',
    'ARCHIVED',
];
export const LEASE_STATES = [
    'NONE',
    'ACQUIRING',
    'ACTIVE',
    'AT_RISK',
    'EXPIRED',
    'TAKING_OVER',
    'FENCED',
    'RELEASED',
    'ERROR',
    'SPLIT_BRAIN_DETECTED',
];
export const CAPACITY_STATES = [
    'SUFFICIENT',
    'TIGHT',
    'PROJECTED_SHORT',
    'EXHAUSTED',
    'RESET_PENDING',
    'UNKNOWN',
];
export const BUILD_STATUSES = [
    'QUEUED',
    'RUNNING',
    'PASSED',
    'FAILED',
    'CANCELLED',
    'STALE',
    'UNKNOWN',
];
export const DEPLOYMENT_STATUSES = [
    'PENDING',
    'DEPLOYING',
    'HEALTHY',
    'DEGRADED',
    'FAILED',
    'ROLLED_BACK',
    'CANCELLED',
    'UNKNOWN',
];
export const JOURNEY_NODE_TYPES = [
    'ORIGIN',
    'ROUTE_SEGMENT',
    'SLICE',
    'DEPENDENCY_GATE',
    'DECISION',
    'HUMAN_HANDOFF',
    'TESTABLE_STATION',
    'INTEGRATION',
    'RELEASE_CANDIDATE',
    'ENVIRONMENT',
    'DESTINATION',
];
export const JOURNEY_EDGE_KINDS = ['SEQUENCE', 'DEPENDENCY', 'INTEGRATION', 'HANDOFF'];
/**
 * Where a fact came from. Never fabricate: unavailable is UNKNOWN, not a guess.
 *
 * The four levels the operating mandate requires are DIRECT, REPORTED, DERIVED
 * and UNKNOWN. FIXTURE is a fifth, deliberately separate level: it exists so a
 * seeded or simulated value can never be silently absorbed into one of the
 * four. A production tenant refuses to store it at all.
 */
export const DATA_QUALITIES = ['DIRECT', 'REPORTED', 'DERIVED', 'FIXTURE', 'UNKNOWN'];
export const PROVENANCE = {
    DIRECT: {
        quality: 'DIRECT',
        label: 'Direct',
        meaning: 'Read from the provider itself over its own interface.',
        isProviderTruth: true,
        admissibleInProduction: true,
    },
    REPORTED: {
        quality: 'REPORTED',
        label: 'Reported',
        meaning: 'Sent in by the agent or client doing the work, not confirmed by the provider.',
        isProviderTruth: false,
        admissibleInProduction: true,
    },
    DERIVED: {
        quality: 'DERIVED',
        label: 'Derived',
        meaning: 'Calculated by DCC from other observations. It is an inference, not a measurement.',
        isProviderTruth: false,
        admissibleInProduction: true,
    },
    FIXTURE: {
        quality: 'FIXTURE',
        label: 'Fixture',
        meaning: 'Seeded demonstration data. It describes nothing that actually happened.',
        isProviderTruth: false,
        admissibleInProduction: false,
    },
    UNKNOWN: {
        quality: 'UNKNOWN',
        label: 'Unknown',
        meaning: 'No source could vouch for this. DCC does not guess in its place.',
        isProviderTruth: false,
        admissibleInProduction: true,
    },
};
/** Provenance is information, except fixture data, which must catch the eye. */
export const PROVENANCE_TONE = {
    DIRECT: 'neutral',
    REPORTED: 'neutral',
    DERIVED: 'neutral',
    FIXTURE: 'attention',
    UNKNOWN: 'neutral',
};
export const ACTIVITY_CLASSES = [
    'PRODUCTIVE',
    'ACTIVE_UNVERIFIED',
    'IDLE_NO_WORK',
    'WAITING_DEPENDENCY',
    'WAITING_HUMAN',
    'BLOCKED_LIMIT',
    'BLOCKED_ERROR',
    'PAUSED_POLICY',
    'OFFLINE',
    'UNKNOWN',
];
export const CAPACITY_LAYERS = ['session_range', 'weekly_fuel', 'seat_plan'];
export const SEVERITIES = ['INFO', 'ATTENTION', 'RISK', 'BLOCKER', 'CRITICAL'];
export const DECISION_STATUSES = ['OPEN', 'ANSWERED', 'DEFERRED', 'EXPIRED', 'CANCELLED'];
export const BLOCKER_STATUSES = ['OPEN', 'MITIGATED', 'RESOLVED'];
export const CONFIDENCE_LEVELS = ['HIGH', 'MEDIUM', 'LOW', 'UNKNOWN'];
/* ------------------------------------------------------------------ */
/* State → tone maps. The UI must not invent its own colour rules.      */
/* ------------------------------------------------------------------ */
export const PROJECT_STATUS_TONE = {
    DRAFT: 'neutral',
    ONBOARDING: 'active',
    ACTIVE: 'active',
    WAITING_DECISION: 'risk',
    AT_RISK: 'risk',
    BLOCKED: 'critical',
    COMPLETED: 'success',
    ARCHIVED: 'neutral',
    UNKNOWN: 'neutral',
};
export const WORK_ITEM_STATUS_TONE = {
    PLANNED: 'neutral',
    READY: 'neutral',
    IN_PROGRESS: 'active',
    WAITING_DEPENDENCY: 'attention',
    WAITING_HUMAN: 'risk',
    REVIEW: 'active',
    BLOCKED: 'critical',
    DONE: 'success',
    SKIPPED: 'neutral',
    CANCELLED: 'neutral',
};
export const TEAM_MEMBER_STATUS_TONE = {
    ACTIVE: 'active',
    // Unused Claude capacity is an opportunity, never a fault and never "idle employee".
    IDLE_CAPACITY: 'neutral',
    WAITING: 'attention',
    BLOCKED_LIMIT: 'risk',
    BLOCKED_OTHER: 'risk',
    OFFLINE: 'neutral',
    UNKNOWN: 'neutral',
};
export const AGENT_STATUS_TONE = {
    STARTING: 'active',
    WORKING: 'active',
    WAITING: 'attention',
    BLOCKED: 'risk',
    PAUSED: 'neutral',
    COMPLETED: 'success',
    FAILED: 'critical',
    STALE: 'attention',
    STOPPED: 'neutral',
};
export const SESSION_STATUS_TONE = {
    AVAILABLE: 'success',
    ACTIVE: 'active',
    RANGE_TIGHT: 'attention',
    RANGE_EXHAUSTED: 'risk',
    RATE_LIMITED: 'risk',
    STALE: 'attention',
    ENDED: 'neutral',
    FAILED: 'critical',
};
export const CONNECTION_STATE_TONE = {
    DRAFT: 'neutral',
    DISCOVERING: 'active',
    MAPPING_PROPOSED: 'active',
    AWAITING_CONFIRMATION: 'attention',
    VERIFYING: 'active',
    INITIAL_SYNC: 'active',
    CONNECTED: 'success',
    DEGRADED: 'risk',
    RECONNECTING: 'attention',
    FAILED: 'critical',
    UNLINKED: 'neutral',
    ARCHIVED: 'neutral',
};
export const LEASE_STATE_TONE = {
    NONE: 'neutral',
    ACQUIRING: 'active',
    ACTIVE: 'success',
    AT_RISK: 'risk',
    EXPIRED: 'attention',
    TAKING_OVER: 'attention',
    FENCED: 'attention',
    RELEASED: 'neutral',
    ERROR: 'risk',
    SPLIT_BRAIN_DETECTED: 'critical',
};
export const CAPACITY_STATE_TONE = {
    SUFFICIENT: 'success',
    TIGHT: 'attention',
    PROJECTED_SHORT: 'risk',
    // Exhausted capacity is a stop, not a fault: orange, never red.
    EXHAUSTED: 'risk',
    RESET_PENDING: 'attention',
    UNKNOWN: 'neutral',
};
export const BUILD_STATUS_TONE = {
    QUEUED: 'neutral',
    RUNNING: 'active',
    PASSED: 'success',
    FAILED: 'critical',
    CANCELLED: 'neutral',
    STALE: 'attention',
    UNKNOWN: 'neutral',
};
export const DEPLOYMENT_STATUS_TONE = {
    PENDING: 'neutral',
    DEPLOYING: 'active',
    HEALTHY: 'success',
    DEGRADED: 'risk',
    FAILED: 'critical',
    ROLLED_BACK: 'attention',
    CANCELLED: 'neutral',
    UNKNOWN: 'neutral',
};
export const SEVERITY_TONE = {
    INFO: 'neutral',
    ATTENTION: 'attention',
    RISK: 'risk',
    BLOCKER: 'critical',
    CRITICAL: 'critical',
};
export const CONFIDENCE_TONE = {
    HIGH: 'success',
    MEDIUM: 'attention',
    LOW: 'risk',
    UNKNOWN: 'neutral',
};
/** Plain-language label so a state never relies on colour alone. */
export const STATE_LABELS = {
    IDLE_CAPACITY: 'Unused Claude capacity',
    BLOCKED_LIMIT: 'Blocked by usage limit',
    BLOCKED_OTHER: 'Blocked (non-capacity)',
    RANGE_EXHAUSTED: 'Session range exhausted',
    RANGE_TIGHT: 'Session range tight',
    PROJECTED_SHORT: 'Projected short',
    RESET_PENDING: 'Waiting for reset',
    SPLIT_BRAIN_DETECTED: 'Split-brain detected',
    WAITING_DEPENDENCY: 'Waiting on dependency',
    WAITING_HUMAN: 'Waiting for a person',
    WAITING_DECISION: 'Waiting for a decision',
    AT_RISK: 'At risk',
    UNKNOWN: 'Data unavailable',
    TESTABLE_STATION: 'Testable station',
    RELEASE_CANDIDATE: 'Release candidate',
    HUMAN_HANDOFF: 'Human handoff',
    DEPENDENCY_GATE: 'Dependency gate',
    ROUTE_SEGMENT: 'Route segment',
};
export function stateLabel(state) {
    return (STATE_LABELS[state] ??
        state
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/^./, (c) => c.toUpperCase()));
}
//# sourceMappingURL=states.js.map